import axios from "axios";
import {
  ACCESS_TOKEN,
  REFRESH_TOKEN,
  USER,
  StorageGetItem,
  StorageSetItem,
} from "./storage";
import { API_BASE } from "../config/consts";
import { logout } from "../features/auth/logout";
import { setSentryUser } from "./sentryUser";
import { ambientHeaders, ACTIVE_COMPANY_KEY } from "./ambientHeaders";
import { TOKEN_REFRESHED } from "./authEvents";

export { ACTIVE_COMPANY_KEY };

export const SELECT_OFFICE_PATH = "/select-office";
export const SELECT_COMPANY_PATH = "/select-company";
export const DASHBOARD_FALLBACK_PATH = "/leads";
export const DASHBOARD_HIDDEN_REFUSAL = "DashboardHidden";

let isRefreshing = false;
let refreshPromise = null;

export const refreshAccessToken = async () => {
  const refreshToken = await StorageGetItem(REFRESH_TOKEN);
  if (!refreshToken) {
    return null;
  }

  try {
    const { data } = await axios.post(`${API_BASE}/refresh-token`, { refreshToken });
    await StorageSetItem(ACCESS_TOKEN, data.accessToken);
    await StorageSetItem(REFRESH_TOKEN, data.refreshToken);
    await StorageSetItem(USER, data.user);
    // The refresh response replaces the stored user, so refresh the Sentry identity with it
    // too — otherwise the scope keeps whatever was set at boot for the rest of the session.
    setSentryUser(data.user);
    // WS #2304782: /feature-flags answers an expired token as signed-out instead of 401, so it never
    // refreshes on its own. Tell it to read again with the new token.
    window.dispatchEvent(new Event(TOKEN_REFRESHED));
    return data.accessToken;
  } catch (e) {
    return null;
  } finally {
    isRefreshing = false;
    refreshPromise = null;
  }
};

const OFFICE_REFUSALS = ['OfficeSelectionRequired', 'OfficeForbidden'];

// Multi-brand. The two refusals mean different things and both end in the same place: a stored brand
// the server will not honour is worse than none, because every subsequent request carries it.
// CompanySelectionRequired means the caller holds more than one firm and named none;
// CompanyForbidden means they named one they do not hold — a stale id after a grant was revoked.
const COMPANY_REFUSALS = ['CompanySelectionRequired', 'CompanyForbidden'];

const readErrorName = async (response) => {
  const data = response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text())?.error?.name;
    } catch (e) {
      return undefined;
    }
  }
  return data?.error?.name;
};

// Exported so a caller that cannot go through this instance can still honour a brand refusal. The
// two document-upload thunks post with bare axios — multipart bodies and a hand-built header set —
// so the response interceptor below never runs for them and a 403 naming a revoked grant was shown
// as "Failed to upload document" with the stale brand left in place.
export const handleCompanyRefusal = async (response) => {
  const errorName = await readErrorName(response);
  if (!COMPANY_REFUSALS.includes(errorName)) return false;

  localStorage.removeItem(ACTIVE_COMPANY_KEY);
  if (window.location.pathname !== SELECT_COMPANY_PATH) {
    window.location.href = SELECT_COMPANY_PATH;
  }
  return true;
};


export const handleResponseError = async (error) => {
  const original = error.config;

  if (error.response?.status === 403) {
    const errorName = await readErrorName(error.response);

    if (OFFICE_REFUSALS.includes(errorName)) {
      localStorage.removeItem('activeOfficeId');
      if (window.location.pathname !== SELECT_OFFICE_PATH) {
        window.location.href = SELECT_OFFICE_PATH;
      }
      return Promise.reject(error);
    }

    if (COMPANY_REFUSALS.includes(errorName)) {
      localStorage.removeItem(ACTIVE_COMPANY_KEY);
      if (window.location.pathname !== SELECT_COMPANY_PATH) {
        window.location.href = SELECT_COMPANY_PATH;
      }
      return Promise.reject(error);
    }

    if (errorName === DASHBOARD_HIDDEN_REFUSAL) {
      if (window.location.pathname !== DASHBOARD_FALLBACK_PATH) {
        window.location.href = DASHBOARD_FALLBACK_PATH;
      }
      return Promise.reject(error);
    }
  }

  if (error.response?.status !== 401) {
    return Promise.reject(error);
  }

  if (original?._retry) {
    logout();
    return Promise.reject(error);
  }

  const storedRefreshToken = await StorageGetItem(REFRESH_TOKEN);
  if (!storedRefreshToken) {
    logout();
    return Promise.reject(error);
  }

  original._retry = true;

  if (!isRefreshing) {
    isRefreshing = true;
    refreshPromise = refreshAccessToken();
  }

  const newAccessToken = await refreshPromise;
  if (!newAccessToken) {
    logout();
    return Promise.reject(error);
  }

  original.headers = original.headers || {};
  original.headers.Authorization = `Bearer ${newAccessToken}`;
  return axios(original);
};

const createApiInstance = async () => {
  const authToken = await StorageGetItem(ACCESS_TOKEN);
  const headers = {
    "Content-type": "application/json",
  };

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  const authorizedInstance = axios.create({
    baseURL: API_BASE,
    headers,
  });

  authorizedInstance.interceptors.request.use(
    (config) => {
      Object.assign(config.headers, ambientHeaders())
      return config
    },
    (error) => Promise.reject(error)
  );

  authorizedInstance.interceptors.response.use(
    (response) => response,
    handleResponseError
  );

  return authorizedInstance;
};

const apiRequest = {
  post: async (url, data, config) => {
    const instance = await createApiInstance();
    return instance.post(url, data, config);
  },
  get: async (url, data, config) => {
    const instance = await createApiInstance();
    return instance.get(url, data, config);
  },
  delete: async (url, data, config) => {
    const instance = await createApiInstance();
    return instance.delete(url, data, config);
  },
  patch: async (url, data, config) => {
    const instance = await createApiInstance();
    return instance.patch(url, data, config);
  },
  put: async (url, data, config) => {
    const instance = await createApiInstance();
    return instance.put(url, data, config);
  },
};

export default apiRequest;
